import { db, doc, getDoc, useAuth } from '@orrekie/shared';
import { useEffect, useMemo, useState } from 'react';

import {
  normalizeProductTier,
  resolveProductTierAudience,
  type ProductTierAudience,
  type ProductTierValue,
} from '../lib/productTier';

type UseProductTierResult = {
  ready: boolean;
  audience: ProductTierAudience;
  productTier: ProductTierValue | null;
};

/**
 * Loads `users/{uid}.productTier` for signed-in users; missing tier resolves as free client-side.
 */
export function useProductTier(): UseProductTierResult {
  const { ready: authReady, hasUser, user } = useAuth();
  const [profileTier, setProfileTier] = useState<string | null | undefined>(undefined);
  const [profileLoaded, setProfileLoaded] = useState(false);

  useEffect(() => {
    if (!authReady) {
      setProfileTier(undefined);
      setProfileLoaded(false);
      return;
    }

    if (!hasUser || !user?.uid) {
      setProfileTier(null);
      setProfileLoaded(true);
      return;
    }

    let cancelled = false;
    setProfileLoaded(false);

    void (async () => {
      try {
        const snap = await getDoc(doc(db(), 'users', user.uid));
        if (!cancelled) {
          const raw = snap.data()?.productTier;
          setProfileTier(typeof raw === 'string' ? raw : null);
        }
      } catch {
        if (!cancelled) setProfileTier(null);
      } finally {
        if (!cancelled) setProfileLoaded(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authReady, hasUser, user?.uid]);

  return useMemo(() => {
    const ready = authReady && profileLoaded;
    const productTier = hasUser ? normalizeProductTier(profileTier) : null;
    const audience = ready ? resolveProductTierAudience(hasUser, profileTier) : 'anonymous';

    return { ready, audience, productTier };
  }, [authReady, profileLoaded, hasUser, profileTier]);
}
